import { useNavigation } from '@react-navigation/native';
import React, {
  ComponentProps,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import Highlighter from '@components/HighlightWords';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import { Badge } from '@components/shared/Badges/Badge';
import Icon from '@components/shared/Icon/Icon';
import { EnhancedAnimatedCheckbox } from '@components/Web/AnimatedCheckbox';
import { Task, TaskPriority } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { getTaskMembership } from '@hooks/useTaskMembership';
import { AppStackScreenProps } from '@navigation/app-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import Images from '@themes/images';
import theme from '@themes/theme';
import {
  beginningOfToday,
  beginningOfYesterday,
  localDateFromUTCDate,
} from '@utils/formatters/date';
import { areTasksVisuallyEqual } from '@utils/taskEquality';

interface TaskProps {
  task: Task;
  filterVal?: string;
  onPress?: (task: Task) => void;
  onLongPress?: (task: Task) => void;
  showProject?: boolean;
  toggleCompleteTask?: (task: Task) => void;
  showBorder?: boolean;
}

const areTaskCardPropsEqual = (prev: TaskProps, next: TaskProps) => {
  if (
    prev.filterVal !== next.filterVal ||
    prev.showProject !== next.showProject ||
    prev.showBorder !== next.showBorder ||
    prev.toggleCompleteTask !== next.toggleCompleteTask ||
    prev.onPress !== next.onPress ||
    prev.onLongPress !== next.onLongPress
  ) {
    return false;
  }

  return areTasksVisuallyEqual(prev.task, next.task);
};

type Priority = {
  label: string;
  variant: ComponentProps<typeof Badge>['variant'];
  color: keyof typeof theme.colors;
  icon: keyof typeof Images;
};

const HIGHLIGHT_STYLE = {
  color: theme.colors.greenSecondary,
  backgroundColor: theme.colors.background,
};

const TaskName = memo(function TaskName(props: {
  name: string;
  filterVal: string;
}) {
  const searchWords = useMemo(
    () => props.filterVal.split(' ').filter(Boolean),
    [props.filterVal]
  );

  return (
    <Text
      accessibilityLabel='Task Name'
      variant='webBodySecondary'
      color='textPrimary'
      numberOfLines={2}>
      {searchWords.length === 0 ? (
        props.name
      ) : (
        <Highlighter
          autoEscape
          highlightStyle={HIGHLIGHT_STYLE}
          searchWords={searchWords}
          textToHighlight={props.name}
        />
      )}
    </Text>
  );
});

const TaskProjectInfo = memo(function TaskProjectInfo(props: {
  icon?: string | null;
  color?: string | null;
  name?: string | null;
}) {
  return (
    <Box flexDirection='row' marginTop='xxs' alignItems='center'>
      <ProjectAvatar icon={props.icon} color={props.color} />

      <Text
        marginLeft='xxs'
        accessibilityLabel='Project Name'
        variant='metadataSmall'
        color='grey04'
        ellipsizeMode='tail'
        numberOfLines={1}
        flexShrink={1}
        style={{ letterSpacing: 0.02 }}>
        {props.name || 'Unknown Project'}
      </Text>
    </Box>
  );
});

const TaskCompleteControl = memo(function TaskCompleteControl(props: {
  isCompleted: boolean;
  taskName: string;
  canComplete: boolean;
  onToggle: () => void;
}) {
  const { isCompleted, taskName, canComplete, onToggle } = props;

  if (!canComplete) {
    if (isCompleted) {
      return (
        <Box mr='xs'>
          <Icon name='StateYes' variant='s' color='grey04' />
        </Box>
      );
    }

    return (
      <Box mr='xs'>
        <RadioButton
          disabled
          onPress={() => {}}
          isSelected={isCompleted}
          iconVariant='s'
        />
      </Box>
    );
  }

  return (
    <Box mr='xs'>
      <EnhancedAnimatedCheckbox
        accessibilityLabel={
          isCompleted
            ? `Mark incomplete | ${taskName}`
            : `Mark complete | ${taskName}`
        }
        isRadioButton={true}
        onHoverChange={() => {}}
        isHovered={false}
        isChecked={isCompleted}
        onValueChange={onToggle}
      />
    </Box>
  );
});

const TaskDueDateChip = memo(function TaskDueDateChip(props: {
  taskId: string;
  taskStatus: Task['status'];
  startDate: Task['startDate'];
  dueDate: Task['dueDate'];
  canComplete: boolean;
  isOverdue: boolean;
  dateColor: ComponentProps<typeof Text>['color'];
  formattedDueDate: string;
  navigation: AppStackScreenProps<'tasks-stack'>['navigation'];
}) {
  const {
    taskId,
    taskStatus,
    startDate,
    dueDate,
    canComplete,
    isOverdue,
    dateColor,
    formattedDueDate,
    navigation,
  } = props;

  const handlePress = useCallback(() => {
    navigation.navigate('task-edit-date-modal', {
      taskId,
      status: taskStatus,
      startDate,
      dueDate,
    });
  }, [dueDate, navigation, startDate, taskId, taskStatus]);

  const handleEmptyDatePress = useCallback(() => {
    navigation.navigate('task-detail-edit-dates', {
      taskId,
      fieldName: 'dueDate',
    });
  }, [navigation, taskId]);

  const emptyDateColor =
    !canComplete && !dueDate ? 'grey04' : ('textSecondary' as const);
  const textColor = !canComplete && !dueDate ? ('grey04' as const) : dateColor;

  return (
    <TouchableOpacity
      testID='due-date-chip'
      disabled={!canComplete}
      onPress={handlePress}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
      <Box
        minWidth={50}
        marginLeft='s'
        px='xs'
        justifyContent='center'
        flexDirection='row'
        height={24}
        borderWidth={1}
        borderColor={isOverdue ? 'alertRed' : 'grey02'}
        borderRadius='s'
        alignItems='center'>
        {!dueDate && (
          <Icon
            onPress={canComplete ? handleEmptyDatePress : undefined}
            variant='xxs'
            marginRight='xxs'
            name='Calendar'
            color={emptyDateColor}
          />
        )}
        <Text
          variant='metadataSmall'
          color={textColor}
          style={{ letterSpacing: 0.02 }}>
          {dueDate ? formattedDueDate : 'Due Date'}
        </Text>
      </Box>
    </TouchableOpacity>
  );
});

export const getPriority = (priority: TaskPriority): Priority => {
  switch (priority) {
    case TaskPriority.High:
      return {
        label: 'High',
        variant: 'red',
        color: 'alertRed',
        icon: 'ChevronsUp',
      };
    case TaskPriority.Medium:
      return {
        label: 'Medium',
        variant: 'yellow',
        color: 'pastelYellow',
        icon: 'Equals',
      };
    case TaskPriority.Low:
      return {
        label: 'Low',
        variant: 'grey',
        color: 'blue',
        icon: 'ChevronsDown',
      };
    default:
      return {
        label: 'Low',
        variant: 'grey01',
        color: 'blue',
        icon: 'ChevronsDown',
      };
  }
};

const TaskCard = ({
  task,
  filterVal = '',
  showProject = true,
  onPress,
  onLongPress,
  toggleCompleteTask,
  showBorder = true,
}: TaskProps) => {
  const { t: f } = useTranslation('format');
  const { navigateToTask } = useAppNavigation();

  const { me } = useMe();
  const navigation =
    useNavigation<AppStackScreenProps<'tasks-stack'>['navigation']>();

  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  const onPressRef = useRef(onPress);
  onPressRef.current = onPress;

  const taskRef = useRef(task);
  taskRef.current = task;

  const navigateToTaskRef = useRef(navigateToTask);
  navigateToTaskRef.current = navigateToTask;

  const canComplete = useMemo(
    () => getTaskMembership(task, me?.id).canEditTaskThirdLevel,
    // canEditTaskThirdLevel only depends on these three role fields — not the full task object
    [
      task.currentUserRole,
      task.currentUserProjectRole,
      task.currentUserProjectTeamRole,
      me?.id,
    ]
  );

  const dueDateState = useMemo(() => {
    const currentDueDate = task.dueDate;
    if (!currentDueDate) {
      return {
        isOverdue: false,
        dateColor: 'textPrimary' as const,
        formattedDueDate: '',
      };
    }

    const taskDate = localDateFromUTCDate(currentDueDate);
    const today = beginningOfToday();
    const yesterday = beginningOfYesterday();
    const isDueToday =
      today.getFullYear() === taskDate.getFullYear() &&
      today.getMonth() === taskDate.getMonth() &&
      today.getDate() === taskDate.getDate();
    const isDueYesterday =
      yesterday.getFullYear() === taskDate.getFullYear() &&
      yesterday.getMonth() === taskDate.getMonth() &&
      yesterday.getDate() === taskDate.getDate();
    const isDueOlderThanYesterday = taskDate < yesterday;
    const isOverdue = isDueYesterday || isDueOlderThanYesterday;

    let dateColor: ComponentProps<typeof Text>['color'] = 'textPrimary';
    if (isDueToday) {
      dateColor = 'greenSecondary';
    } else if (isOverdue) {
      dateColor = 'alertRed';
    }

    return {
      isOverdue,
      dateColor,
      formattedDueDate: f('monthAndDayYearV3', {
        val: taskDate,
      }),
    };
  }, [f, task.dueDate]);

  const handleCardPress = useCallback(() => {
    onPressRef.current?.(taskRef.current);
    navigateToTaskRef.current(taskRef.current);
  }, []);

  const handleCardLongPress = useCallback(() => {
    onLongPressRef.current?.(taskRef.current);
  }, []);

  const handleToggleComplete = useCallback(() => {
    if (!canComplete) return;
    toggleCompleteTask?.(taskRef.current);
  }, [toggleCompleteTask, canComplete]);

  return (
    <TouchableOpacity
      onPress={handleCardPress}
      onLongPress={handleCardLongPress}
      delayLongPress={200}>
      <Box flex={1} flexDirection='row' alignItems='center'>
        {toggleCompleteTask && (
          <TaskCompleteControl
            isCompleted={!!task.completedAt}
            taskName={task.name}
            canComplete={canComplete}
            onToggle={handleToggleComplete}
          />
        )}
        <Box
          py='s'
          flex={1}
          flexDirection='row'
          alignItems='center'
          borderTopColor='grey02'
          borderTopWidth={showBorder ? 1 : 0}>
          <Box flex={1}>
            <TaskName name={task.name} filterVal={filterVal} />
            {showProject && (
              <TaskProjectInfo
                icon={task.project?.icon}
                color={task.project?.color}
                name={task.project?.name}
              />
            )}
          </Box>
          <TaskDueDateChip
            taskId={task.id}
            taskStatus={task.status}
            startDate={task.startDate}
            dueDate={task.dueDate}
            canComplete={canComplete}
            isOverdue={dueDateState.isOverdue}
            dateColor={dueDateState.dateColor}
            formattedDueDate={dueDateState.formattedDueDate}
            navigation={navigation}
          />
        </Box>
      </Box>
    </TouchableOpacity>
  );
};

export default memo(TaskCard, areTaskCardPropsEqual);
